/**
 * NfseFormPage — nova emissão de NFS-e (Trilho Nacional), no desenho oficial de
 * `/quotes/new`.
 *
 * Era o `NfseNewDialog`. Virou ROTA (`/nfse/nova`) pelos mesmos motivos do
 * #163/#164, com uma diferença que só esta tela tem: por ser alcançável por URL,
 * ela precisa REPETIR os portões da `NfsePage` (papel → plano → contribuinte →
 * readiness). Um formulário fiscal que renderiza para quem não pode emitir
 * aceita os dados e só falha no envio — e a ordem papel-antes-de-plano é regra
 * de produto com teste próprio (#38): mostrar upsell a um `member` sugere que o
 * problema dele é dinheiro e que assinar resolveria.
 *
 * O PREFILL DO ORÇAMENTO mora aqui (era do `NfseModule`): quem semeia o
 * formulário é o formulário. O id vem por `?quote=` — é dado de origem, não de
 * autorização (a emissão é gated pelos portões acima e pela RLS), e na URL ele
 * sobrevive a recarregar.
 *
 * Coleta tomador + serviço (LC 116) + valores, monta o rascunho e dispara a
 * emissão via Edge Function (`useNfse().issue`). A validação do tomador é
 * repetida no servidor (§16.C) — aqui é só a camada de UX.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { FieldLabel, FIELD_INPUT, CONTENT_CARD } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Loader2, Lock, Search } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import { useRole } from '@/hooks/useRole';
import { useFiscalProfile } from '@/hooks/useFiscalProfile';
import { useFiscalReadiness } from '@/hooks/useFiscalReadiness';
import { useNfse } from '@/hooks/useNfse';
import { getCorrelacaoByLC116, searchServicos } from '@/lib/fiscalData';
import { formatDocument } from '@/lib/documentUtils';
import { parseBRLInput } from '@/lib/currencyInput';
import { MoneyInput } from '@/components/ui/money-input';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { cn } from '@/lib/utils';

const ISSUE_ERRORS: Record<string, string> = {
  certificate_missing: 'Nenhum certificado ativo. Envie o certificado A1 antes de emitir.',
  certificate_expired: 'O certificado está expirado.',
  onboarding_incomplete: 'Conclua o cadastro fiscal antes de emitir.',
  cnpj_mismatch: 'O CNPJ do certificado não corresponde ao do perfil.',
  servico_missing: 'Selecione um serviço.',
  sequence_alloc_failed: 'Falha ao numerar a nota. Tente novamente.',
  taxpayer_cannot_emit: 'Este tipo de contribuinte não emite NFS-e.',
};

/** Semente vinda de um orçamento de origem (fluxo orçamento→NFS-e). */
export interface NfsePrefill {
  quoteId?: string | null;
  clientId?: string | null;
  tomadorNome?: string;
  tomadorDoc?: string;
  tomadorEmail?: string;
  valor?: string;
  descricao?: string;
  /** Item da lista LC116 (herdado do serviço do catálogo, se houver). */
  itemLista?: string;
  /** Alíquota ISS (%) como string (herdada do serviço do catálogo). */
  aliquota?: string;
  /** ISS retido na fonte, herdado do CADASTRO do serviço (`products.iss_retido`). */
  issRetido?: boolean;
}

const brl = (n: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);

export default function NfseFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quoteIdParam = searchParams.get('quote');

  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const nfseFeature = useFeature('nfse_emission');
  const { canSeeFinance } = useRole();
  const { profile } = useFiscalProfile();
  const readiness = useFiscalReadiness();
  const { issue } = useNfse();

  /** Fechar = voltar para de onde veio (a lista, o orçamento, a OS…). */
  const close = () => navigate(-1);

  const [clientId, setClientId] = useState<string>('');
  const [tomadorNome, setTomadorNome] = useState('');
  const [tomadorDoc, setTomadorDoc] = useState('');
  const [tomadorEmail, setTomadorEmail] = useState('');
  const [servicoQuery, setServicoQuery] = useState('');
  const [itemLista, setItemLista] = useState('');
  const [descricao, setDescricao] = useState('');
  // Campos de DINHEIRO guardam número e são MASCARADOS (#157); `null` = vazio.
  // A ALÍQUOTA logo abaixo continua string e SEM máscara de dinheiro, de
  // propósito: ela é percentual, e passá-la por aqui faria 2,5% virar 25%.
  const [valor, setValor] = useState<number | null>(null);
  const [deducoes, setDeducoes] = useState<number | null>(null);
  const [aliquota, setAliquota] = useState('');
  const [issRetido, setIssRetido] = useState(false);
  const [competencia, setCompetencia] = useState(() => new Date().toISOString().slice(0, 10));

  const { data: clients = [] } = useQuery({
    queryKey: ['clients-min', activeCompany?.id],
    enabled: Boolean(activeCompany?.id),
    queryFn: async () => {
      const { data } = await supabase
        .from('clients')
        .select('id, name, document, email')
        .eq('company_id', activeCompany!.id)
        .order('name');
      return data ?? [];
    },
  });

  /**
   * Fluxo orçamento→NFS-e: carrega o orçamento (e o cliente) para semear a
   * emissão e vinculá-la via `quote_id`. Vivia no `NfseModule`, que abria o
   * diálogo sozinho quando a resposta chegava; aqui a rota JÁ é o formulário.
   */
  const { data: quotePrefill } = useQuery({
    queryKey: ['nfse-quote-prefill', quoteIdParam],
    enabled: Boolean(quoteIdParam),
    queryFn: async (): Promise<NfsePrefill | null> => {
      const { data: q } = await supabase
        .from('quotes')
        .select('id, client_id, client_name, client_document, total, title')
        .eq('id', quoteIdParam as string)
        .maybeSingle();
      if (!q) return null;

      let client: { name: string; document: string | null; email: string | null } | null = null;
      if (q.client_id) {
        const { data: c } = await supabase
          .from('clients')
          .select('name, document, email')
          .eq('id', q.client_id)
          .maybeSingle();
        client = c;
      }

      // Serviço fiscal: 1º item-serviço do orçamento que veio do catálogo classificado.
      let servico: {
        lc116_item: string | null;
        iss_aliquota: number | null;
        /** `products.iss_retido` — vira `tpRetISSQN=2` no DPS. */
        iss_retido: boolean;
        descricao: string | null;
      } | null = null;
      const { data: items } = await supabase
        .from('quote_items')
        .select('item_type, product_id, name')
        .eq('quote_id', q.id)
        .order('position', { ascending: true });
      const firstService = (items ?? []).find(
        (it) => (it.item_type ?? 'service') === 'service' && it.product_id,
      );
      if (firstService?.product_id) {
        const { data: p } = await supabase
          .from('products')
          .select('lc116_item, iss_aliquota, iss_retido, name')
          .eq('id', firstService.product_id)
          .maybeSingle();
        if (p) {
          servico = {
            lc116_item: p.lc116_item,
            iss_aliquota: p.iss_aliquota,
            iss_retido: (p as { iss_retido?: boolean | null }).iss_retido === true,
            descricao: firstService.name || p.name || null,
          };
        }
      }

      const doc = client?.document || q.client_document || '';
      return {
        quoteId: q.id,
        clientId: q.client_id,
        tomadorNome: client?.name || q.client_name || '',
        tomadorDoc: doc ? formatDocument(doc) : '',
        tomadorEmail: client?.email || '',
        valor: q.total != null ? Number(q.total).toFixed(2).replace('.', ',') : '',
        descricao: q.title || servico?.descricao || 'Serviços conforme orçamento',
        itemLista: servico?.lc116_item ?? undefined,
        aliquota:
          servico?.iss_aliquota != null ? String(servico.iss_aliquota).replace('.', ',') : undefined,
        // A retencao vem do CADASTRO do servico: e propriedade dele, nao uma
        // decisao a refazer a cada nota. Sem heranca, o campo nasce sempre
        // desmarcado e quem presta servico retido erra toda vez.
        issRetido: servico?.iss_retido === true,
      };
    },
  });

  /**
   * Semeia UMA vez, quando a resposta do orçamento chega. No diálogo o gatilho
   * era `open` (e o `prefill` já existia); aqui a página nasce montada e o dado
   * vem depois — sem o marcador, cada refetch reescreveria o que o usuário está
   * digitando.
   */
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !quotePrefill) return;
    seededRef.current = true;
    if (quotePrefill.clientId !== undefined) setClientId(quotePrefill.clientId ?? '');
    if (quotePrefill.tomadorNome) setTomadorNome(quotePrefill.tomadorNome);
    if (quotePrefill.tomadorDoc) setTomadorDoc(quotePrefill.tomadorDoc);
    if (quotePrefill.tomadorEmail) setTomadorEmail(quotePrefill.tomadorEmail);
    if (quotePrefill.valor) setValor(parseBRLInput(quotePrefill.valor));
    if (quotePrefill.descricao) setDescricao(quotePrefill.descricao);
    if (quotePrefill.itemLista) setItemLista(quotePrefill.itemLista);
    if (quotePrefill.aliquota) setAliquota(quotePrefill.aliquota);
    setIssRetido(quotePrefill.issRetido === true);
  }, [quotePrefill]);

  const servicoResults = useMemo(
    () => (servicoQuery.trim() ? searchServicos(servicoQuery).slice(0, 8) : []),
    [servicoQuery],
  );

  // Dinheiro em pt-BR passa pelo parser de dinheiro, nunca por `Number` na mão:
  // `Number('1.500'.replace(',', '.'))` é **1,5** (mil vezes menos, sem aviso, num
  // documento fiscal) e `'1.500,00'` vira NaN. Ver #71 e docs/testes.md §3.17.
  // ⚠️ A alíquota NÃO usa este parser: ela é percentual, onde o ponto é decimal —
  // `parseBRLInput('2.5')` daria 25, dez vezes o imposto.
  const valorNum = valor ?? 0;
  const valid = Boolean(itemLista && descricao.trim() && valorNum > 0);

  const submit = async () => {
    if (!valid || !profile) return;
    const corr = getCorrelacaoByLC116(itemLista);
    try {
      await issue.mutateAsync({
        provider: (profile.provider ?? 'nacional') as 'nacional' | 'sao_paulo' | 'fortaleza',
        client_id: clientId || null,
        quote_id: quotePrefill?.quoteId ?? null,
        tomador_snapshot: {
          nome: tomadorNome.trim() || null,
          documento: tomadorDoc.replace(/\D/g, '') || null,
          email: tomadorEmail.trim() || null,
        },
        servico_snapshot: {
          item_lista: itemLista,
          // O picker LC116 já entrega o código NACIONAL de 6 díg. (ex.: "140101").
          // Passa como codigo_tributacao_nacional p/ o DPS usar EXATO — senão o
          // resolveCTribNac trunca p/ 4 díg. + "00" e perde o desdobramento.
          codigo_tributacao_nacional: itemLista,
          descricao: descricao.trim(),
          aliquota_iss: aliquota ? Number(aliquota.replace(',', '.')) : null,
          nbs: corr?.nbs ?? null,
          cclasstrib: corr?.cClassTrib ?? null,
        },
        valor_servico: valorNum,
        // Sem parser, aqui não havia nem o `|| 0` do valor: o NaN de "1.200,00"
        // seguia direto para o rascunho da nota.
        deducoes: deducoes ?? 0,
        aliquota_iss: aliquota ? Number(aliquota.replace(',', '.')) : null,
        iss_retido: issRetido,
        competencia,
      });
      toast.success('Emissão enviada. Acompanhe o status na lista.');
      // `replace`: o formulário cumpriu o papel, e voltar da lista tem que
      // devolver a tela de ORIGEM, não um rascunho de nota já emitida.
      navigate('/nfse', { replace: true });
    } catch (e) {
      const code = (e as Error)?.message ?? 'erro';
      // Mostra o motivo real (ex.: mensagem de rejeição do Sefin) quando não houver
      // um texto amigável mapeado — em vez de mascarar a falha como genérica.
      toast.error(
        ISSUE_ERRORS[code] ?? (code && code !== 'erro' ? code : 'Não foi possível emitir a NFS-e.'),
      );
    }
  };

  /* ==================================================================== */
  /* HERO — o mesmo cabeçalho sangrado de /quotes/new.                     */
  /* -------------------------------------------------------------------- */
  /* A nota é um DOCUMENTO com valor, então o valor ao vivo é o VALOR DO    */
  /* SERVIÇO, e a sublinha é o TOMADOR — que é quem distingue uma nota da   */
  /* outra na lista de /nfse.                                              */
  const heroValor = brl(valorNum);

  const hero = (
    <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
      <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

      <div className="relative flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
          onClick={close}
          aria-label="Voltar"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-white/70">Novo documento</p>
          <h1 className="truncate text-lg font-bold leading-tight">NFS-e</h1>
        </div>
        {/* O AMBIENTE segue a mesma regra do `NfseModule`: nota emitida em
            homologação não vale nada, e essa informação muda o significado de
            tudo o que a tela mostra — então ela fica visível SEMPRE. */}
        {profile && (
          <span
            className={cn(
              'shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium leading-none',
              profile.ambiente === 'prod'
                ? 'bg-white/15 text-white/90'
                : 'bg-amber-400 text-amber-950',
            )}
          >
            {profile.ambiente === 'prod' ? 'Produção' : 'Homologação'}
          </span>
        )}
      </div>

      <div className="relative mt-5">
        <p className="text-xs text-white/75">Valor do serviço</p>
        <p className={cn('truncate font-bold leading-tight', heroTotalSizeClass(heroValor))}>
          {heroValor}
        </p>
        <p className="mt-1 truncate text-xs text-white/70">
          {tomadorNome.trim() || 'Preencha os dados abaixo'}
        </p>
      </div>
    </div>
  );

  /**
   * PORTÃO ÚNICO para os estados em que a emissão não é possível. A rota é
   * alcançável por URL, então ela repete a cadeia da `NfsePage` — papel antes de
   * plano (#38).
   */
  const gate = (() => {
    if (!user) return 'Faça login para emitir uma NFS-e.';
    if (!canSeeFinance)
      return 'A NFS-e está disponível apenas para o proprietário e administradores desta empresa.';
    if (!nfseFeature.allowed)
      return 'A emissão de NFS-e está disponível nos planos pagos. Desbloqueie para emitir.';
    if (readiness.isLoading) return null;
    if (profile?.taxpayer_type === 'autonomo_informal')
      return 'Como autônomo sem CNPJ, você não emite NFS-e. Use o Recibo como comprovante.';
    if (!readiness.ready || !profile)
      return 'Conclua o cadastro fiscal da empresa (CNPJ, município e certificado) antes de emitir.';
    return null;
  })();

  if (gate || readiness.isLoading || !profile) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="pb-4">
          {hero}
          <div className="relative z-10 -mt-9 space-y-5">
            <div className={cn(CONTENT_CARD, 'flex flex-col items-center p-6 text-center')}>
              {readiness.isLoading && !gate ? (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              ) : (
                <>
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                    <Lock className="h-7 w-7 text-muted-foreground" />
                  </div>
                  <h2 className="text-lg font-semibold">Emissão indisponível</h2>
                  <p className="mt-1 max-w-xs text-sm text-muted-foreground">{gate}</p>
                  <Button3D className="mt-5" onClick={() => navigate('/nfse')}>
                    Ir para Nota fiscal
                  </Button3D>
                </>
              )}
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout showFab={false} width="form">
      <div className="pb-4">
        {hero}

        {/* Conteúdo — o primeiro cartão sobe e encosta na curva do hero */}
        <div className="relative z-10 -mt-9 space-y-5">
          {/* ── TOMADOR ─────────────────────────────────────────────────── */}
          <div className={cn(CONTENT_CARD, 'space-y-4 p-4')}>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="nfse-cliente">Cliente (opcional)</FieldLabel>
              <Select
                value={clientId || 'none'}
                onValueChange={(v) => {
                  const next = v === 'none' ? '' : v;
                  setClientId(next);
                  const c = clients.find((x) => x.id === next);
                  if (c) {
                    setTomadorNome(c.name);
                    // Autofill do documento/e-mail do tomador a partir do cliente.
                    if (c.document) setTomadorDoc(formatDocument(c.document));
                    if (c.email) setTomadorEmail(c.email);
                  }
                }}
              >
                <SelectTrigger id="nfse-cliente" className={FIELD_INPUT}>
                  <SelectValue placeholder="Sem cliente vinculado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem cliente vinculado</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <FieldLabel htmlFor="t-nome">Tomador (nome)</FieldLabel>
              <Input
                id="t-nome"
                value={tomadorNome}
                onChange={(e) => setTomadorNome(e.target.value)}
                className={FIELD_INPUT}
              />
            </div>

            <div className="grid grid-cols-2 items-start gap-3">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="t-doc">CPF/CNPJ do tomador</FieldLabel>
                <Input
                  id="t-doc"
                  inputMode="numeric"
                  value={tomadorDoc}
                  onChange={(e) => setTomadorDoc(e.target.value)}
                  className={cn(FIELD_INPUT, 'min-w-0')}
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="t-email">E-mail</FieldLabel>
                <Input
                  id="t-email"
                  type="email"
                  value={tomadorEmail}
                  onChange={(e) => setTomadorEmail(e.target.value)}
                  className={cn(FIELD_INPUT, 'min-w-0')}
                />
              </div>
            </div>
          </div>

          {/* ── SERVIÇO (LC 116) ────────────────────────────────────────── */}
          <div className={cn(CONTENT_CARD, 'space-y-4 p-4')}>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="nfse-servico">Serviço (LC 116)</FieldLabel>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="nfse-servico"
                  className={cn(FIELD_INPUT, 'pl-9')}
                  placeholder="Buscar por descrição ou código"
                  value={servicoQuery}
                  onChange={(e) => setServicoQuery(e.target.value)}
                />
              </div>
              {servicoResults.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded-xl border">
                  {servicoResults.map((s) => (
                    <button
                      key={s.codigo}
                      type="button"
                      className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
                      onClick={() => {
                        setItemLista(s.codigo);
                        if (!descricao) setDescricao(s.descricao);
                        setServicoQuery('');
                      }}
                    >
                      <span className="font-mono text-xs text-muted-foreground">{s.codigo}</span> —{' '}
                      {s.descricao}
                    </button>
                  ))}
                </div>
              )}
              {itemLista && (
                <p className="text-xs text-muted-foreground">
                  Item selecionado: <span className="font-mono">{itemLista}</span>
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <FieldLabel htmlFor="desc">Descrição do serviço</FieldLabel>
              <Textarea
                id="desc"
                rows={2}
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
              />
            </div>
          </div>

          {/* ── VALORES E TRIBUTAÇÃO ────────────────────────────────────── */}
          <div className={cn(CONTENT_CARD, 'space-y-4 p-4')}>
            {/* Três campos numa linha de 360px não cabem com rótulo legível
                (#162): valor e deduções dividem a linha e a alíquota fica
                sozinha, onde o rótulo "Alíquota ISS (%)" não corta. */}
            <div className="grid grid-cols-2 items-start gap-3">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="valor">Valor (R$)</FieldLabel>
                <MoneyInput id="valor" value={valor} onCommit={setValor} className="min-w-0" />
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="ded">Deduções (R$)</FieldLabel>
                <MoneyInput id="ded" value={deducoes} onCommit={setDeducoes} className="min-w-0" />
              </div>
            </div>

            <div className="grid grid-cols-2 items-start gap-3">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="aliq">Alíquota ISS (%)</FieldLabel>
                <Input
                  id="aliq"
                  inputMode="decimal"
                  value={aliquota}
                  onChange={(e) => setAliquota(e.target.value)}
                  className={cn(FIELD_INPUT, 'min-w-0')}
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="comp">Competência</FieldLabel>
                <Input
                  id="comp"
                  type="date"
                  value={competencia}
                  onChange={(e) => setCompetencia(e.target.value)}
                  className={cn(FIELD_INPUT, 'min-w-0')}
                />
              </div>
            </div>

            <label
              htmlFor="ret"
              className="flex cursor-pointer items-center gap-2 rounded-xl border p-2.5"
            >
              <Checkbox
                id="ret"
                checked={issRetido}
                onCheckedChange={(v) => setIssRetido(v === true)}
              />
              <span className="text-sm">ISS retido pelo tomador</span>
            </label>
          </div>
        </div>

        {/* Barra FLUTUANTE — filha DIRETA do container da página. */}
        <div className="sticky bottom-[calc(var(--bottom-nav-h)+0.5rem)] z-20 mt-6 rounded-2xl border border-border/60 bg-card/95 p-2 shadow-[0_-6px_20px_-10px_rgba(0,0,0,0.35)] backdrop-blur">
          <div className="flex gap-2">
            <Button3D
              color="gray"
              size="sm"
              className="flex-1"
              onClick={close}
              disabled={issue.isPending}
            >
              Cancelar
            </Button3D>
            <Button3D
              size="sm"
              className="flex-1"
              disabled={!valid || issue.isPending}
              onClick={submit}
            >
              {issue.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Emitir NFS-e
            </Button3D>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
